// Volume control for the sleep-timer fade. iOS ignores HTMLMediaElement.volume (read-only 1),
// so there we route the element through a Web Audio GainNode — created lazily, only when a fade
// is actually needed, because routing changes how iOS treats background audio.

export interface Fader {
  set(volume: number): void;
}

function canSetVolume(audio: HTMLAudioElement): boolean {
  const before = audio.volume;
  audio.volume = before === 0.5 ? 0.4 : 0.5;
  const settable = audio.volume !== before;
  audio.volume = before;
  return settable;
}

export function createFader(audio: HTMLAudioElement): Fader {
  let gain: GainNode | null = null;
  let useGain: boolean | null = null;

  return {
    set(volume) {
      const v = Math.min(1, Math.max(0, volume));
      useGain ??= !canSetVolume(audio);
      if (!useGain) {
        audio.volume = v;
        return;
      }
      if (!gain && v === 1) return; // nothing to do, don't create the graph
      if (!gain) {
        try {
          const w = window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          };
          const Ctx = w.AudioContext ?? w.webkitAudioContext;
          if (!Ctx) return;
          const ctx = new Ctx();
          gain = ctx.createGain();
          ctx.createMediaElementSource(audio).connect(gain).connect(ctx.destination);
          void ctx.resume();
        } catch {
          useGain = false;
          return;
        }
      }
      gain.gain.value = v;
    },
  };
}
